import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {DatePipe} from '@angular/common';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {FormStatusService} from '../../../../../../shared/services/form-status.service';
import {ErrorService} from '../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {PropuestasComercialesService} from '../propuestas-comerciales.service';
import {PropuestaComercialArchivosService} from '../propuesta-comercial-archivos.service';
import {PropuestaComercialArchivo} from '../propuesta-comercial-archivos.interface';
import {EditionButtons} from '../../../../../../shared/components/ui/edition-buttons/edition-buttons';
import {LabelComponent} from '../../../../../../shared/components/form/label/label.component';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {Spinner} from '../../../../../../shared/components/ui/spinner/spinner';
import {MatTooltip} from '@angular/material/tooltip';
import {DeleteTrigger} from '../../../../../../shared/components/ui/delete-trigger/delete-trigger';
import {ButtonComponent} from '../../../../../../shared/components/ui/button/button.component';
import {SwitchComponent} from '../../../../../../shared/components/form/switch/switch.component';
import {Card} from '../../../../../../shared/components/common/card/card';
import {IconButton} from "../../../../../../shared/components/ui/icon-button/icon-button";
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {TextareaFieldComponent} from '@shared/components/form/textarea/textarea-field.component';
import {CheckboxComponent} from '@shared/components/form/checkbox/checkbox.component';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';

@Component({
  selector: 'app-detalle-propuesta-comercial',
  imports: [
    HasPermissionDirective,
    EditionButtons,
    FormsModule,
    LabelComponent,
    MatIcon,
    MatIconButton,
    ReactiveFormsModule,
    Spinner,
    MatTooltip,
    DeleteTrigger,
    ButtonComponent,
    SwitchComponent,
    DatePipe,
    Card,
    IconButton,
    SelectFieldComponent,
    TextareaFieldComponent,
    CheckboxComponent,
    InputFieldComponent,
    AppFormComponent,
  ],
  templateUrl: './detalle-propuesta-comercial.html',
  styles: ``
})
export class DetallePropuestaComercial implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  @Input() idPropuesta!: number;
  @Output() onClose = new EventEmitter<boolean>();

  editForm!: FormGroup;
  loading = false;
  editable = true;

  estadoOptions: SelectOption[] = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'aceptada', label: 'Aceptada' },
    { value: 'rechazada', label: 'Rechazada' },
  ];
  updating = false;
  fileSelected: File | null = null;
  archivoPrincipal: string | null = null;
  contactoFecha: string | null = null;
  userName: string | null = null;
  userModName: string | null = null;
  updatedAt: string | null = null;

  // Archivos
  archivos: PropuestaComercialArchivo[] = [];
  loadingArchivos = false;
  uploadingArchivo = false;
  deletingArchivo = false;
  deletingArchivoIndex: number | null = null;
  newArchivoFile: File | null = null;
  newArchivoDescripcion = '';

  private fB = inject(FormBuilder);
  private fS = inject(FormStatusService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private propuestasComercialesService = inject(PropuestasComercialesService);
  private archivosService = inject(PropuestaComercialArchivosService);

  ngOnInit(): void {
    this.getPropuesta();
    this.makeForm();
    this.getArchivos();
  }

  private makeForm(): void {
    this.editForm = this.fB.group({
      activo: [{ value: true, disabled: this.editable }],
      presupuesto_nro: [{ value: '', disabled: this.editable }],
      descripcion: [{ value: '', disabled: this.editable }],
      fecha_generacion_presupuesto: [{ value: '', disabled: this.editable }],
      fecha_envio_presupuesto: [{ value: '', disabled: this.editable }],
      confirmacion_recepcion: [{ value: false, disabled: this.editable }],
      fecha_confirmacion_recepcion: [{ value: '', disabled: this.editable }],
      monto: [{ value: '', disabled: this.editable }],
      descuento: [{ value: '', disabled: this.editable }],
      estado: [{ value: 'pendiente', disabled: this.editable }],
      motivo_rechazo: [{ value: '', disabled: this.editable }]
    });
  }

  getPropuesta() {
    this.loading = true;
    this.propuestasComercialesService.show(this.idPropuesta).subscribe({
      next: (response) => {
        if (response.status) {
          this.editForm.patchValue({
            ...response.data,
            fecha_generacion_presupuesto: this.normalizeDateValue(response.data.fecha_generacion_presupuesto),
            fecha_envio_presupuesto: this.normalizeDateValue(response.data.fecha_envio_presupuesto),
            fecha_confirmacion_recepcion: this.normalizeDateValue(response.data.fecha_confirmacion_recepcion),
          });
          this.archivoPrincipal = response.data.path ?? null;
          this.contactoFecha = response.data.contacto_fecha ?? null;
          this.userName = response.data.user_name ?? null;
          this.userModName = response.data.user_mod_name ?? null;
          this.updatedAt = response.data.updated_at ?? null;
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  update(): void {
    this.updating = true;
    // Actualizar archivo si se seleccionó uno
    if (this.fileSelected) this.updateArchivo();
    this.clearFormToSend();

    this.propuestasComercialesService.update(this.idPropuesta, this.editForm.value).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Propuesta comercial actualizado correctamente');
          this.onClose.emit(true);
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.updating = false;
    });
  }

  updateArchivo(): void {
    if (!this.fileSelected) return;

    const formData = new FormData();
    formData.append('path', this.fileSelected);

    this.propuestasComercialesService.updateArchivo(this.idPropuesta, formData).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Archivo actualizado correctamente');
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private clearFormToSend(): void {
    // Si los campos de fecha están vacíos, se deshabilitan para no enviarlos en el request
    const fechaEnvioPresupuesto = this.editForm.get('fecha_envio_presupuesto');
    const fechaGeneracionPresupuesto = this.editForm.get('fecha_generacion_presupuesto');
    const fechaConfirmacionRecepcion = this.editForm.get('fecha_confirmacion_recepcion');

    if (!fechaEnvioPresupuesto?.value) {
      fechaEnvioPresupuesto?.disable();
    }

    if (!fechaGeneracionPresupuesto?.value) {
      fechaGeneracionPresupuesto?.disable();
    }

    if (!fechaConfirmacionRecepcion?.value) {
      fechaConfirmacionRecepcion?.disable();
    }
  }

  editableSwitcher(): void {
    this.fS.changeStatus(this.editForm, this.editable);
    this.editable = !this.editable;
  }

  refreshPropuesta(): void {
    this.editableSwitcher();
    this.getPropuesta();
  }

  close(): void {
    this.onClose.emit(false);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.type !== 'application/pdf') {
      this.snackbarService.showSnackBar('Solo se permiten archivos PDF', 'error');
      if (input) input.value = '';
      return;
    }

    this.fileSelected = file;
  }

  // Métodos para archivos múltiples
  getArchivos(): void {
    this.loadingArchivos = true;
    this.archivosService.index(this.idPropuesta).subscribe({
      next: (response) => {
        if (response.status) {
          this.archivos = response.data;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loadingArchivos = false;
    });
  }

  onNewArchivoSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.type !== 'application/pdf') {
      this.snackbarService.showSnackBar('Solo se permiten archivos PDF', 'error');
      if (input) input.value = '';
      return;
    }

    this.newArchivoFile = file;
  }

  uploadArchivo(): void {
    if (!this.newArchivoFile) return;

    this.uploadingArchivo = true;
    const formData = new FormData();
    formData.append('crm_propuesta_comercial_id', this.idPropuesta.toString());
    formData.append('path', this.newArchivoFile);
    if (this.newArchivoDescripcion) {
      formData.append('descripcion', this.newArchivoDescripcion);
    }

    this.archivosService.store(formData).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Archivo agregado correctamente');
          this.newArchivoFile = null;
          this.newArchivoDescripcion = '';
          this.getArchivos();
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.uploadingArchivo = false;
    });
  }

  deleteArchivo(id: number, index: number): void {
    this.deletingArchivo = true;
    this.deletingArchivoIndex = index;

    this.archivosService.destroy(id).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Archivo eliminado correctamente');
          this.getArchivos();
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.deletingArchivo = false;
      this.deletingArchivoIndex = null;
    });
  }

  downloadArchivo(id: number): void {
    this.archivosService.download(id).subscribe({
      next: (response) => {
        const fileURL = URL.createObjectURL(response);
        window.open(fileURL, '_blank');
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  downloadArchivoPrincipal(): void {
    this.propuestasComercialesService.download(this.idPropuesta).subscribe({
      next: (response) => {
        const fileURL = URL.createObjectURL(response);
        window.open(fileURL, '_blank');
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  getArchivoNombre(path: string | null): string {
    if (!path) return 'Archivo';
    const normalized = path.replace(/\\/g, '/');
    const parts = normalized.split('/');
    return parts[parts.length - 1] || path;
  }

  private normalizeDateValue(value: string | null): string | null {
    if (!value) return null;
    const dateOnly = value.split('T')[0];
    const match = dateOnly.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      const [, day, month, year] = match;
      return `${year}-${month}-${day}`;
    }
    return dateOnly;
  }
}
